import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

// Eagerly loaded — small, always needed
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Auth/dashboard shell — lazily loaded so @supabase/supabase-js and framer-motion
// stay OUT of the marketing critical path (they ride along these chunks).
const AuthProvider = lazy(() =>
  import("@/auth/AuthProvider").then((m) => ({ default: m.AuthProvider }))
);
const ProtectedRoute = lazy(() =>
  import("@/components/auth/ProtectedRoute").then((m) => ({ default: m.ProtectedRoute }))
);
const DashboardLayout = lazy(() =>
  import("@/components/dashboard/DashboardLayout").then((m) => ({ default: m.DashboardLayout }))
);

// Lazily loaded — only fetched when the user navigates to these routes
const PublicPricing = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.Pricing })));
const Contact = lazy(() => import("./pages/Contact").then((m) => ({ default: m.Contact })));
const PrivacyPolicy = lazy(() => import("./pages/Privacy"));
const FeaturesPage = lazy(() => import("./pages/Features"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const TryPage = lazy(() => import("./pages/TryPage"));
const StatsPage = lazy(() => import("./pages/Stats"));
const AuthPage = lazy(() =>
  import("./pages/AuthPage").then((m) => ({ default: m.AuthPage }))
);
const VerifyEmailPage = lazy(() =>
  import("./pages/VerifyEmailPage").then((m) => ({ default: m.VerifyEmailPage }))
);
const DashboardHome = lazy(() =>
  import("./pages/dashboard/DashboardHome").then((m) => ({ default: m.DashboardHome }))
);
const Documents = lazy(() =>
  import("./pages/dashboard/Documents").then((m) => ({ default: m.Documents }))
);
const Pricing = lazy(() =>
  import("./pages/dashboard/Pricing").then((m) => ({ default: m.Pricing }))
);
const Settings = lazy(() =>
  import("./pages/dashboard/Settings").then((m) => ({ default: m.Settings }))
);
const Profile = lazy(() =>
  import("./pages/dashboard/Profile").then((m) => ({ default: m.Profile }))
);
const CheckoutSuccess = lazy(() =>
  import("./pages/dashboard/CheckoutSuccess").then((m) => ({ default: m.CheckoutSuccess }))
);
const ApiKeys = lazy(() =>
  import("./pages/dashboard/ApiKeys").then((m) => ({ default: m.ApiKeys }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    {/* TooltipProvider is lightweight — stays at root so tooltips work everywhere */}
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public marketing routes ─────────────────────────────────────
                No AuthProvider here. @supabase/supabase-js is NOT in this bundle.
                Auth state is read from localStorage via useLocalSession(). */}
            <Route path="/" element={<Index />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PublicPricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/try" element={<TryPage />} />
            <Route path="/stats" element={<StatsPage />} />

            {/* ── Auth routes ─────────────────────────────────────────────────
                AuthProvider mounted here so login/register forms can call
                supabase.auth.signIn / signUp. */}
            <Route
              path="/signin"
              element={
                <AuthProvider>
                  <AuthPage />
                </AuthProvider>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthProvider>
                  <AuthPage />
                </AuthProvider>
              }
            />
            <Route
              path="/verify-email"
              element={
                <AuthProvider>
                  <VerifyEmailPage />
                </AuthProvider>
              }
            />

            {/* ── Dashboard ───────────────────────────────────────────────────
                AuthProvider wraps ProtectedRoute + DashboardLayout.
                Nested child routes render via <Outlet> inside DashboardLayout,
                so they inherit the auth context without extra wrappers. */}
            <Route
              path="/dashboard"
              element={
                <AuthProvider>
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                </AuthProvider>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="documents" element={<Documents />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
              <Route path="success" element={<CheckoutSuccess />} />
              <Route path="api-keys" element={<ApiKeys />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    <Analytics />
    <SpeedInsights />
  </QueryClientProvider>
);

export default App;
